import { Button } from "@secure-vault/ui";
import {
  listTrusteeUnlockPetitions,
  listTrusteeShares,
  UnlockPetitionItem,
  ShareItem,
} from "@secure-vault/api";
import { useAuth } from "@/lib/auth-context";
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  BadgeCheck,
  Clock,
  Loader2,
  Lock,
  ShieldCheck,
  Unlock,
} from "lucide-react";
import { toast } from "sonner";
import { useDateFormat } from "@/hooks/use-date-format";
import { useCallback, useEffect, useState } from "react";
import { useIdentityVerification } from "../hooks/use-identity-verification";
import { IdentityVerificationBadge } from "../components/identity-verification-badge";

/**
 * Trustee dashboard.
 *
 * Identity verification (KYC) is a real, per-vault Stripe Identity check — a
 * trustee proves a document-backed identity for each seat they hold. Verification
 * is *fail-soft* here (a trustee can still see their shard while `pending`), so
 * the owner/adviser can spot coverage gaps before an unlock is ever needed; the
 * hard, fail-closed gate lives at unlock time (see the unlock portal). This
 * replaces the previous client-side `localStorage` placebo, which verified
 * nothing server-side.
 */
export function TrusteeDashboardContainer() {
  const { formatDateTime } = useDateFormat();
  const { user } = useAuth();
  const trusteeEmail = user?.email || "";

  const [shares, setShares] = useState<ShareItem[]>([]);
  const [petitions, setPetitions] = useState<UnlockPetitionItem[]>([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [shareList, petitionList] = await Promise.all([
        listTrusteeShares(trusteeEmail),
        listTrusteeUnlockPetitions(),
      ]);
      setShares(shareList);
      setPetitions(petitionList);
    } catch (err: unknown) {
      console.error(err);
      toast.error("We couldn't load your vaults. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [trusteeEmail]);

  useEffect(() => {
    if (trusteeEmail) loadData();
  }, [trusteeEmail, loadData]);

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Vaults you help with
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            See the vaults where someone has chosen you as a trustee and whether
            they need your help.
          </p>
        </div>
      </div>

      <div className="flex gap-3 rounded-lg border border-blue-500/10 bg-blue-500/5 p-4 text-xs leading-relaxed text-blue-500">
        <ShieldCheck className="h-5 w-5 shrink-0" />
        <div>
          <strong className="mb-0.5 block font-semibold">
            We need to confirm your identity before you can help.
          </strong>
          You can confirm your identity now. We will ask you to confirm it again
          when a vault needs to be opened. Secure Legacy does not keep copies of
          the identity documents you provide.
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : shares.length === 0 ? (
        <div className="bg-card border-border space-y-4 rounded-xl border p-12 text-center shadow-sm">
          <div className="bg-primary/10 text-primary mx-auto flex h-12 w-12 items-center justify-center rounded-full">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">No vaults yet</h3>
            <p className="text-muted-foreground mx-auto max-w-sm text-xs">
              No one has added {trusteeEmail} as a trustee yet. When someone
              chooses you, their vault will appear here.
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          <h2 className="border-b pb-2 text-base font-semibold">Your vaults</h2>
          {shares.map((share) => (
            <TrusteeVaultCard
              key={share.share_id}
              share={share}
              petition={petitions.find((p) => p.client_id === share.client_id)}
              formatDateTime={formatDateTime}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Unlock/lock state derived from any active unlock petition on the vault. */
function getShareStatus(petition?: UnlockPetitionItem) {
  if (!petition)
    return {
      label: "No access request",
      style: "bg-muted border-border text-muted-foreground",
      icon: Lock,
      status: "locked" as const,
    };
  if (petition.status === "approved")
    return {
      label: "Ready to open",
      style: "bg-emerald-500/10 border-emerald-500/20 text-emerald-500",
      icon: Unlock,
      status: "unlocked" as const,
    };
  if (petition.status === "rejected")
    return {
      label: "Request not approved",
      style: "bg-amber-500/10 border-amber-500/20 text-amber-500",
      icon: Lock,
      status: "locked" as const,
    };
  return {
    label: "Awaiting approval",
    style: "bg-amber-500/10 border-amber-500/20 text-amber-500",
    icon: Clock,
    status: "pending" as const,
  };
}

/**
 * A single assigned-vault card. Owns its own nomination-verification state via
 * {@link useIdentityVerification}, scoped to this vault's `client_id`.
 */
function TrusteeVaultCard({
  share,
  petition,
  formatDateTime,
}: {
  share: ShareItem;
  petition?: UnlockPetitionItem;
  formatDateTime: (v: number) => string;
}) {
  const status = getShareStatus(petition);
  const StatusIcon = status.icon;

  const {
    status: idStatus,
    running,
    verify,
  } = useIdentityVerification({
    clientId: share.client_id,
    purpose: "nomination",
  });

  const handleVerify = async () => {
    const result = await verify();
    if (result === "verified") {
      toast.success("Your identity has been confirmed.");
    } else if (result === "failed" || result === "expired") {
      toast.error("We couldn't confirm your identity. Please try again.");
    }
  };

  const showVerify =
    idStatus === "none" || idStatus === "failed" || idStatus === "expired";

  return (
    <div className="bg-card border-border hover:border-primary/30 flex flex-col items-start justify-between gap-6 rounded-xl border p-6 shadow-sm transition-all md:flex-row md:items-center">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-medium ${status.style}`}
          >
            <StatusIcon className="h-3 w-3" />
            {status.label}
          </span>
          <IdentityVerificationBadge status={idStatus} />
        </div>

        <div>
          <h3 className="text-foreground text-sm font-semibold">
            Vault reference: {share.client_id.slice(-8).toUpperCase()}
          </h3>
          <p className="text-muted-foreground mt-0.5 text-xs">
            You were chosen on {formatDateTime(share.created_at)}
          </p>
        </div>
      </div>

      <div className="flex w-full flex-col items-stretch gap-3 border-t pt-4 md:w-auto md:flex-row md:items-center md:border-t-0 md:pt-0">
        {showVerify && (
          <Button
            onClick={handleVerify}
            disabled={running}
            variant="outline"
            className="w-full text-xs md:w-auto"
          >
            {running ? (
              <>
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                Checking…
              </>
            ) : (
              <>
                <BadgeCheck className="mr-1.5 h-4 w-4" />
                Confirm your identity
              </>
            )}
          </Button>
        )}

        {status.status === "locked" && (
          <Button
            asChild
            className="w-full text-xs md:w-auto"
            variant="outline"
          >
            <Link to="/trustee/unlock" hash={`clientId=${share.client_id}`}>
              Request access
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Link>
          </Button>
        )}
        {status.status === "pending" && (
          <Button
            asChild
            className="w-full text-xs md:w-auto"
            variant="outline"
          >
            <Link to="/trustee/unlock" hash={`clientId=${share.client_id}`}>
              Check progress
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Link>
          </Button>
        )}
        {status.status === "unlocked" && (
          <Button asChild className="w-full text-xs md:w-auto">
            <Link to="/trustee/unlock" hash={`clientId=${share.client_id}`}>
              Open documents
              <Unlock className="ml-1.5 h-4 w-4" />
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}
